import React, { useEffect, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Modal } from "./ui";
import { Layers, PinOff } from "lucide-react";

const COLS = 12;
const BOXES = 10;
const BASE_CODES = [
  "AUD", "BMD", "BSD", "BZD", "CAD", "CHF", "CNY", "EURO", "GBP", "GGP", "GIP", "HKD",
];

// Create-time prompt: after a Currencida account is created in the account list, offer to
// peg it onto the Master Dashboard grid (next free box of a chosen currency column).
export default function PegToMasterDashboard({ account, onClose, onPlaced }) {
  const [cols, setCols] = useState([]);   // [{ code, freeBoxes }]
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [opt, cur] = await Promise.all([client.get("/options/currency"), client.get("/currencies")]);
        const saved = opt.data?.master_dashboard;
        const codes = Array.from({ length: COLS }, (_, i) =>
          (Array.isArray(saved) && saved[i]?.code) || BASE_CODES[i] || `Col ${i + 1}`);
        const used = {}; // `${c}-${b}` occupied
        (cur.data || []).forEach((r) => {
          if (r.md_col != null && r.md_box != null) used[`${r.md_col}-${r.md_box}`] = true;
        });
        const list = codes.map((code, c) => {
          let free = 0;
          for (let b = 0; b < BOXES; b++) if (!used[`${c}-${b}`]) free++;
          return { c, code, free };
        });
        if (alive) { setCols(list); setLoading(false); }
      } catch (e) { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const peg = async () => {
    if (choice === "") return;
    const col = cols.find((x) => String(x.c) === String(choice));
    if (!col || col.free <= 0) return;
    setSaving(true);
    try {
      // Find next free box in the chosen column.
      const cur = await client.get("/currencies");
      const used = {};
      (cur.data || []).forEach((r) => { if (r.md_col != null && r.md_box != null) used[`${r.md_col}-${r.md_box}`] = true; });
      let box = -1;
      for (let b = 0; b < BOXES; b++) if (!used[`${col.c}-${b}`]) { box = b; break; }
      if (box < 0) { setSaving(false); return; }
      await client.put(`/currencies/${account.id}`, { data: { md_col: col.c, md_box: box } });
      onPlaced && onPlaced();
    } catch (e) { /* noop */ } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={TL("Peg to Master Dashboard?")} testid="peg-md-modal">
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-lg border border-[var(--primary)]/25 bg-[var(--primary)]/[0.05] p-3">
          <Layers size={18} className="mt-0.5 flex-none text-[var(--primary)]" />
          <p className="text-sm">
            <b>{account?.name || "This account"}</b> was created. Would you like to pin it onto the
            Master Dashboard so it becomes targetable by SEBAA, BoxAlong and Cluster Control?
            You can always place or unpeg it later.
          </p>
        </div>
        {loading ? (
          <div className="py-4 text-center text-sm opacity-60" data-testid="peg-md-loading">{TL("Loading columns…")}</div>
        ) : (
          <div className="space-y-2">
            <label className="text-xs font-medium opacity-70">{TL("Currency column")}</label>
            <select value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="peg-md-column"
              className="w-full rounded-lg border border-black/15 bg-white/70 px-3 py-2 text-sm dark:border-white/15 dark:bg-black/30">
              <option value="">{TL("Choose a column…")}</option>
              {cols.map((c) => (
                <option key={c.c} value={c.c} disabled={c.free <= 0}>
                  {c.code} {c.free > 0 ? `— ${c.free} free box${c.free === 1 ? "" : "es"}` : "— full"}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="peg-md-skip"
            className="inline-flex items-center gap-1 rounded-lg border border-black/15 px-3 py-2 text-sm dark:border-white/20">
            <PinOff size={14} /> Not now
          </button>
          <button type="button" onClick={peg} disabled={choice === "" || saving} data-testid="peg-md-confirm"
            className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? "Pinning…" : "Pin to dashboard"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
